import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Users, CalendarDays, TrendingUp, AlertCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

export default function LeaveReportsTab() {
  const [summary, setSummary] = useState(null);
  const [deptData, setDeptData] = useState([]);
  const [trends, setTrends] = useState([]);
  const [absenteeism, setAbsenteeism] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportTab, setReportTab] = useState('summary');
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter] = useState('All');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const [s, d, t, a, dp] = await Promise.all([
        api.get('/api/leave/reports/summary', { params }),
        api.get('/api/leave/reports/department'),
        api.get('/api/leave/reports/trends'),
        api.get('/api/leave/reports/absenteeism'),
        api.get('/api/leave/departments'),
      ]);
      setSummary(s.data);
      setDeptData(d.data);
      setTrends(t.data);
      setAbsenteeism(a.data);
      setDepartments(dp.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [startDate, endDate]);

  const filteredDeptData = deptFilter === 'All' ? deptData : deptData.filter(d => d.department === deptFilter);
  const filteredAbsenteeism = deptFilter === 'All' ? absenteeism : absenteeism.filter(e => e.department === deptFilter);

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-5" data-testid="leave-reports">
      {/* Date Filters + Department + Export */}
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs text-slate-500">From</Label>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 w-40" data-testid="report-start-date" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">To</Label>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 w-40" data-testid="report-end-date" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Department</Label>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="report-dept-filter">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Departments</SelectItem>
              {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 ml-auto">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => {
            const a = document.createElement('a');
            a.href = `${process.env.REACT_APP_BACKEND_URL}/api/leave/reports/export?format=csv&report_type=${reportTab === 'trends' ? 'summary' : reportTab}`;
            a.setAttribute('download', '');
            const token = localStorage.getItem('token');
            fetch(a.href, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.blob()).then(blob => {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `leave_report_${reportTab}.csv`;
              link.click();
            });
          }} data-testid="export-csv-btn">
            <Download className="w-3 h-3" /> CSV
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => {
            const token = localStorage.getItem('token');
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leave/reports/export?format=pdf&report_type=${reportTab === 'trends' ? 'summary' : reportTab}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.blob()).then(blob => {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `leave_report_${reportTab}.pdf`;
              link.click();
            });
          }} data-testid="export-pdf-btn">
            <Download className="w-3 h-3" /> PDF
          </Button>
        </div>
      </div>

      {/* Report Sub-tabs */}
      <Tabs value={reportTab} onValueChange={setReportTab}>
        <TabsList className="bg-slate-50 border border-slate-200 h-9">
          <TabsTrigger value="summary" className="text-xs" data-testid="report-tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="department" className="text-xs" data-testid="report-tab-dept">Department</TabsTrigger>
          <TabsTrigger value="trends" className="text-xs" data-testid="report-tab-trends">Monthly Trends</TabsTrigger>
          <TabsTrigger value="absenteeism" className="text-xs" data-testid="report-tab-absenteeism">Absenteeism</TabsTrigger>
        </TabsList>

        {/* Summary */}
        <TabsContent value="summary" className="mt-3 space-y-4">
          {summary && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <SummaryCard icon={CalendarDays} label="Total Requests" value={summary.total_requests} color="text-blue-600" />
                <SummaryCard icon={TrendingUp} label="Approved Days" value={summary.total_days} color="text-emerald-600" />
                <SummaryCard icon={BarChart3} label="Approved" value={summary.approved} color="text-emerald-600" />
                <SummaryCard icon={Users} label="Pending" value={summary.pending} color="text-amber-600" />
                <SummaryCard icon={AlertCircle} label="Rejected" value={summary.rejected} color="text-red-500" />
              </div>
              <Card className="border-slate-200">
                <CardHeader className="py-3 px-4 pb-2"><CardTitle className="text-sm font-semibold">Leave Type Breakdown</CardTitle></CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(summary.by_type || {}).map(([type, count]) => (
                      <div key={type} className="text-center p-3 rounded-lg bg-slate-50">
                        <p className="text-2xl font-semibold font-tabular text-[#0B1B33]">{count}</p>
                        <p className="text-xs text-slate-500 mt-1">{type === 'CL' ? 'Casual' : type === 'EL' ? 'Earned' : 'Sick'} Leave</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Department-wise */}
        <TabsContent value="department" className="mt-3">
          {filteredDeptData.length > 0 ? (
            <Card className="border-slate-200">
              <CardContent className="px-4 py-4">
                <div className="space-y-4">
                  {filteredDeptData.map(d => {
                    const maxDays = Math.max(...filteredDeptData.map(x => x.total_days), 1);
                    return (
                      <div key={d.department} data-testid={`dept-report-${d.department}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium text-slate-700">{d.department}</span>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span>{d.unique_employees} employees</span>
                            <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">{d.total_days} days total</Badge>
                          </div>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                          {d.cl_days > 0 && <div className="h-full bg-blue-500 transition-all" style={{ width: `${(d.cl_days / maxDays) * 100}%` }} title={`CL: ${d.cl_days}`} />}
                          {d.el_days > 0 && <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(d.el_days / maxDays) * 100}%` }} title={`EL: ${d.el_days}`} />}
                          {d.sl_days > 0 && <div className="h-full bg-amber-500 transition-all" style={{ width: `${(d.sl_days / maxDays) * 100}%` }} title={`SL: ${d.sl_days}`} />}
                        </div>
                        <div className="flex gap-4 mt-1 text-[10px] text-slate-400">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />CL: {d.cl_days}</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />EL: {d.el_days}</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />SL: {d.sl_days}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : <p className="text-sm text-slate-400 p-6 text-center">No department data yet</p>}
        </TabsContent>

        {/* Monthly Trends */}
        <TabsContent value="trends" className="mt-3">
          <Card className="border-slate-200">
            <CardHeader className="py-3 px-4 pb-2"><CardTitle className="text-sm font-semibold">Monthly Leave Trends (Last 12 Months)</CardTitle></CardHeader>
            <CardContent className="px-4 pb-4">
              {trends.length > 0 ? (
                <div className="space-y-1">
                  <div className="flex items-end gap-1" style={{ height: '200px' }}>
                    {trends.map((t, i) => {
                      const maxDays = Math.max(...trends.map(x => x.days), 1);
                      const height = maxDays > 0 ? (t.days / maxDays) * 100 : 0;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`trend-bar-${i}`}>
                          <span className="text-[9px] font-tabular text-slate-500 mb-1">{t.days > 0 ? t.days : ''}</span>
                          <div
                            className={`w-full rounded-t transition-all ${t.days > 0 ? 'bg-[#0D2850]' : 'bg-slate-100'}`}
                            style={{ height: `${Math.max(height, 3)}%`, minHeight: '2px' }}
                            title={`${t.month}: ${t.days} days (${t.count} leaves)`}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-1">
                    {trends.map((t, i) => (
                      <div key={i} className="flex-1 text-center">
                        <span className="text-[8px] text-slate-400">{t.month.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-8">No trend data</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Absenteeism */}
        <TabsContent value="absenteeism" className="mt-3">
          <Card className="border-slate-200">
            <CardHeader className="py-3 px-4 pb-2"><CardTitle className="text-sm font-semibold">Employee Absenteeism Report</CardTitle></CardHeader>
            <CardContent className="p-0">
              {filteredAbsenteeism.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>#</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead className="text-center">Total Days</TableHead>
                      <TableHead className="text-center">CL</TableHead>
                      <TableHead className="text-center">EL</TableHead>
                      <TableHead className="text-center">Leave Count</TableHead>
                      <TableHead>Risk</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAbsenteeism.map((e, i) => {
                      const risk = e.total_days >= 10 ? 'High' : e.total_days >= 5 ? 'Medium' : 'Low';
                      const riskColor = risk === 'High' ? 'bg-red-100 text-red-700' : risk === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
                      return (
                        <TableRow key={e.employee_id} data-testid={`absentee-row-${i}`}>
                          <TableCell className="text-xs text-slate-400">{i + 1}</TableCell>
                          <TableCell className="text-sm font-medium">{e.employee_name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{e.department}</TableCell>
                          <TableCell className="text-center text-sm font-semibold font-tabular">{e.total_days}</TableCell>
                          <TableCell className="text-center text-sm font-tabular">{e.cl_days || 0}</TableCell>
                          <TableCell className="text-center text-sm font-tabular">{e.el_days || 0}</TableCell>
                          <TableCell className="text-center text-sm font-tabular">{e.leave_count}</TableCell>
                          <TableCell><Badge className={`text-[10px] ${riskColor} hover:opacity-90`}>{risk}</Badge></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : <p className="text-sm text-slate-400 text-center py-8">No absenteeism data</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-slate-50 ${color}`}><Icon className="w-5 h-5" /></div>
        <div>
          <p className="text-2xl font-semibold font-tabular text-[#0B1B33]">{value}</p>
          <p className="text-[10px] text-slate-500 uppercase">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
